/**
 * ForensicTelemetryCard — frame-accurate telemetry card used by:
 *   • Analytics → History → Forensic Panel → Telemetry tab
 *   • Event Videos → Video Modal → Forensic Telemetry Snapshot
 *
 * Renders a single device_data point in the standard "8 cards + IO mapped values"
 * grid layout. Pure presentation: no fetching, no privilege checks. Callers gate
 * mount/render via the existing `analytics_history_forensic_panel` privilege.
 *
 * Props:
 *   point             — required. One enriched point as returned by
 *                       GET /api/gps/history-detail/{device_id}.points[i]
 *                       or the slim /api/media/event-videos/{id}/event-telemetry
 *                       endpoint. Shape:
 *                       { lat, lng, speed, sensor_speed, heading, altitude,
 *                         ignition, satellites, motion_state, timestamp,
 *                         server_timestamp, protocol, event_id, mapped_values }
 *   timezone          — IANA timezone string for display formatting.
 *   ioMappings        — optional. Same shape as forensicData.io_mappings.
 *                       Used to compute value_colors for state-typed IO values.
 *   showMotionBadge   — optional, default true. Hide for the slim video-modal
 *                       variant if motion state is irrelevant.
 *   emptyHint         — optional override text for the "no point selected" empty
 *                       state. Default targets the Analytics map use-case.
 */
import React, { useCallback } from 'react';
import { Badge } from '../ui/badge';
import {
  Clock,
  MapPin,
  Gauge,
  Compass,
  Mountain,
  Satellite,
  Power,
  Activity,
  Crosshair,
} from 'lucide-react';
import { formatDT, formatDateOnly, formatTimeOnly, formatSpeed } from '../../utils/timezone';

const COLOR_NAME_TO_HEX = {
  red: '#EF4444',
  green: '#22C55E',
  blue: '#3B82F6',
  orange: '#F97316',
  purple: '#A855F7',
  teal: '#14B8A6',
  amber: '#F59E0B',
  rose: '#F43F5E',
  yellow: '#FACC15',
};

const motionBadgeClass = (state) => {
  switch (state) {
    case 'moving':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'idle':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    default:
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  }
};

export const ForensicTelemetryCard = ({
  point,
  timezone,
  ioMappings,
  showMotionBadge = true,
  hasIgnitionMapping = true,
  emptyHint,
}) => {
  const getMappedValueColor = useCallback(
    (ioId, displayValue, rawValue) => {
      if (!ioMappings || displayValue == null) return null;
      const mapping = ioMappings.find((m) => m.io_id === ioId);
      if (!mapping?.value_colors) return null;
      const vc = mapping.value_colors;
      if (typeof vc !== 'object') return null;
      const colorName =
        vc[String(displayValue)] ||
        (rawValue != null ? vc[String(rawValue)] : null) ||
        vc[String(displayValue).toUpperCase()] ||
        vc[String(displayValue).toLowerCase()] ||
        vc._default ||
        null;
      if (!colorName) return null;
      return COLOR_NAME_TO_HEX[colorName] || colorName;
    },
    [ioMappings],
  );

  if (!point) {
    return (
      <div
        className="py-8 text-center text-sm text-muted-foreground"
        data-testid="forensic-telemetry-empty"
      >
        <Crosshair className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
        <p>
          {emptyHint ||
            'Click a data point on the map, a timeline item, or use the navigation arrows to select a point.'}
        </p>
      </div>
    );
  }

  const tzOpts = { timezone, seconds: true };
  const lat = typeof point.lat === 'number' ? point.lat : Number(point.lat);
  const lng = typeof point.lng === 'number' ? point.lng : Number(point.lng);
  const hasFix = Number.isFinite(lat) && Number.isFinite(lng);

  return (
    <div className="space-y-3" data-testid="forensic-telemetry-card">
      {/* Selected point summary row */}
      <div className="flex items-center gap-2 flex-wrap">
        {showMotionBadge && (
          <Badge className={`text-[10px] ${motionBadgeClass(point.motion_state)}`}>
            {point.motion_state?.toUpperCase() || 'UNKNOWN'}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {formatDT(point.timestamp, tzOpts)}
        </span>
        {point.protocol && (
          <Badge variant="outline" className="text-[10px]">
            {point.protocol}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {/* Position Time */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="text-[10px] text-muted-foreground">Time (Position)</span>
          </div>
          <p className="text-xs font-semibold">
            {formatDateOnly(point.timestamp, tzOpts)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatTimeOnly(point.timestamp, tzOpts)}
          </p>
        </div>

        {/* Server Time */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="text-[10px] text-muted-foreground">Time (Server)</span>
          </div>
          <p className="text-xs font-semibold">
            {formatDateOnly(point.server_timestamp, tzOpts)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatTimeOnly(point.server_timestamp, tzOpts)}
          </p>
        </div>

        {/* Position */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <MapPin className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Position</span>
          </div>
          {hasFix ? (
            <p className="text-xs font-semibold">
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2563eb] hover:underline"
                data-testid="forensic-position-link"
              >
                {lat.toFixed(6)}, {lng.toFixed(6)}
              </a>
            </p>
          ) : (
            <p className="text-xs font-semibold text-muted-foreground">No fix</p>
          )}
        </div>

        {/* Speed */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Gauge className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Speed</span>
          </div>
          <p className="text-xs font-semibold">{point.speed != null ? formatSpeed(point.speed) : '-'}</p>
          {point.sensor_speed != null && (
            <p className="text-[9px] text-muted-foreground">
              Sensor: {formatSpeed(point.sensor_speed)}
            </p>
          )}
        </div>

        {/* Heading */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Compass className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Heading</span>
          </div>
          <p className="text-xs font-semibold">{point.heading}°</p>
        </div>

        {/* Altitude */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Mountain className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Altitude</span>
          </div>
          <p className="text-xs font-semibold">{point.altitude} m</p>
        </div>

        {/* Satellites */}
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Satellite className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Satellites</span>
          </div>
          <p className="text-xs font-semibold">
            {point.satellites != null ? point.satellites : (point.gps_fix ? 'Not reported' : '—')}
          </p>
        </div>

        {/* GPS Fix status (Digital Matter FID 0 diagnostics) */}
        {point.gps_fix && (() => {
          const fix = point.gps_fix;
          const fixTs = fix.gps_timestamp ? new Date(fix.gps_timestamp).getTime() : null;
          const recTs = point.timestamp ? new Date(point.timestamp).getTime() : null;
          const lagSecs = fixTs != null && recTs != null ? Math.max(0, Math.round((recTs - fixTs) / 1000)) : null;
          const lagLabel = lagSecs == null ? null
            : lagSecs < 60 ? `${lagSecs}s`
            : lagSecs < 3600 ? `${Math.round(lagSecs / 60)}m`
            : `${(lagSecs / 3600).toFixed(1)}h`;
          const cached = fix.old_fix || (lagSecs != null && lagSecs > 60);
          return (
            <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden" data-testid="forensic-gps-fix-card">
              <div className="flex items-center gap-1 mb-1">
                <Crosshair className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">GPS Fix</span>
              </div>
              <p className={`text-xs font-semibold ${!fix.fix_valid ? 'text-red-500' : cached ? 'text-amber-600' : 'text-green-600'}`}>
                {!fix.fix_valid ? 'Invalid' : `Valid${fix.fix_3d ? ' · 3D' : ''}`}
                {fix.old_fix ? ' · Old fix' : ''}
              </p>
              <p className="text-[9px] text-muted-foreground">
                {fix.position_accuracy_m != null ? `±${fix.position_accuracy_m}m` : ''}
                {fix.pdop != null ? ` · PDOP ${fix.pdop}` : ''}
                {fix.status_flags != null ? ` · 0x${Number(fix.status_flags).toString(16).toUpperCase().padStart(2, '0')}` : ''}
              </p>
              {fix.gps_timestamp && (
                <p className={`text-[9px] ${cached ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                  Fix: {formatTimeOnly(fix.gps_timestamp, tzOpts)}
                  {lagSecs != null && lagSecs > 1 ? ` (${lagLabel} before record)` : ''}
                </p>
              )}
            </div>
          );
        })()}

        {/* Ignition — only for devices with an Ignition IO mapping */}
        {hasIgnitionMapping && (
        <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 mb-1">
            <Power className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Ignition</span>
          </div>
          <p
            className={`text-xs font-semibold ${
              point.ignition ? 'text-green-600' : 'text-red-500'
            }`}
          >
            {point.ignition ? 'ON' : 'OFF'}
          </p>
        </div>
        )}

        {/* IO Mapped Values */}
        {Object.entries(point.mapped_values || {}).map(([key, mv]) => {
          const valColor = getMappedValueColor(mv.io_id, mv.value, mv.raw);
          return (
            <div
              key={key}
              className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden"
              data-testid={`forensic-io-${mv.io_id}`}
            >
              <div className="flex items-center gap-1 mb-1">
                <Activity className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">{key}</span>
              </div>
              <p className="text-xs font-semibold break-all">
                {mv.type === 'state' ? (
                  <span
                    style={valColor ? { color: valColor } : undefined}
                    className={
                      !valColor
                        ? mv.value === 'ON' || mv.value === 'Open'
                          ? 'text-green-600'
                          : mv.value === 'OFF' || mv.value === 'Closed'
                            ? 'text-red-500'
                            : ''
                        : ''
                    }
                  >
                    {mv.value}
                  </span>
                ) : (
                  <span className="break-all" style={valColor ? { color: valColor } : undefined}>
                    {mv.value} {mv.units}
                  </span>
                )}
              </p>
              <p className="text-[9px] text-muted-foreground break-all">Raw: {String(mv.raw)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ForensicTelemetryCard;
